/**
 * Carries the analytics context for an in-flight inspection submit from the form
 * hook to the shared mutation error handler.
 *
 * The hook cannot track the failure itself on the optimistic paths: those
 * navigate away immediately, and TanStack Query skips a per-call `onError` once
 * the observer has no listeners (`mutationObserver` guards on `hasListeners()`),
 * so a rejection after navigation would be recorded nowhere. The shared handler
 * in `inspection-mutations.ts` always runs — including on offline resume after a
 * restart — but is module-level and has no access to the hook's `trackEvent` or
 * its form state, hence this hand-off keyed by the client-minted inspectionId.
 *
 * The context is consumed on read, so a resumed-then-failed submission reports
 * once rather than on every retry.
 */

export type InspectionSubmitAnalyticsContext = Record<string, unknown>;

const contexts = new Map<string, InspectionSubmitAnalyticsContext>();

export function rememberInspectionSubmitContext(
  inspectionId: string,
  context: InspectionSubmitAnalyticsContext
): void {
  contexts.set(inspectionId, context);
}

export function takeInspectionSubmitContext(
  inspectionId: string
): InspectionSubmitAnalyticsContext | undefined {
  const context = contexts.get(inspectionId);
  contexts.delete(inspectionId);
  return context;
}

/** Dropped on success — nothing failed, so the context is no longer needed. */
export function forgetInspectionSubmitContext(inspectionId: string): void {
  contexts.delete(inspectionId);
}
